import React from 'react'
import { Field, Form, Formik, FormikErrors } from 'formik'
import { Card, Columns, Element, Heading, Loader } from 'react-bulma-components'
import { Mission } from '../../api/missions'
import { Organization } from '../../api/organizations'
import AutocompleteField from '../form/fields/autocomplete'
import { RequestButton, RequestMessage } from '../request-components/request-components'
import { useOrganizationQuery } from '../../queries/organizations'
import { useServicesQuery } from '../../queries/services'
import { useUpdateMutation } from '../../queries/missions'
import useStore from '../../store'
import { buildListAsInputOptions } from '../../utils/forms'
import { getUserLabel } from '../../utils/users'
import WorkPeriodTags from '../work-periods/work-period-tags'
import { getDocIdAsString } from '../../utils/documents'

export interface MissionInfoPanelProps {
  mission: Mission
}

interface MissionInfoFormValues {
  service: string
  address: string
}

/**
 * Mission details tab: editable assignment (service) and address; read-only worker, job title, tags, complementary info.
 */
const MissionInfoPanel: React.FC<MissionInfoPanelProps> = ({ mission }) => {
  const currentOrganization = useStore(state => state.session.currentOrganization) as Organization
  const organizationQuery = useOrganizationQuery(currentOrganization._id, currentOrganization.type)
  const servicesQuery = useServicesQuery()
  const updateMutation = useUpdateMutation()

  const missionNotEditable = !mission.__actions.canBeUpdated

  const servicesList = React.useMemo(() => {
    if (missionNotEditable) {
      return mission.service ? [mission.service] : []
    }
    return servicesQuery.data ?? []
  }, [missionNotEditable, mission.service, servicesQuery.data])

  if (!servicesQuery.isFetched || !organizationQuery.isFetched) return <Loader />

  const handleSubmit = (values: MissionInfoFormValues): void => {
    if (missionNotEditable) return
    updateMutation.mutate({
      _id: mission._id,
      service: values.service,
      address: values.address,
    })
  }

  return (
    <Card>
      <Card.Content>
        <Element mb={5}>
          <Heading renderAs="h3" size={4} mb={3}>
            Travailleur
          </Heading>
          <Element>{getUserLabel(mission.user)}</Element>
        </Element>
        <Element mb={5}>
          <Heading renderAs="h3" size={4} mb={3}>
            Métier
          </Heading>
          <Element>{mission.jobTitle.name}</Element>
        </Element>
        <Element mb={5}>
          <Heading renderAs="h3" size={4} mb={3}>
            Catégories
          </Heading>
          <WorkPeriodTags tags={mission.tags ?? []} isEditable={false} />
        </Element>
        {mission.complementaryInformations ? (
          <Element mb={5}>
            <Heading renderAs="h3" size={4} mb={3}>
              Informations complémentaires
            </Heading>
            <Element textColor="grey">{mission.complementaryInformations}</Element>
          </Element>
        ) : null}
        <Formik<MissionInfoFormValues>
          enableReinitialize
          initialValues={{
            service: getDocIdAsString(mission.service),
            address: mission.address ?? '',
          }}
          validate={(values): FormikErrors<MissionInfoFormValues> => {
            const errors: FormikErrors<MissionInfoFormValues> = {}
            if (!values.service) errors.service = 'Champ requis'
            if (!values.address) errors.address = 'Champ requis'
            return errors
          }}
          onSubmit={handleSubmit}
        >
          <Form>
            <Heading renderAs="h3" size={4} mb={3}>
              Données modifiables
            </Heading>
            <Columns>
              <Columns.Column>
                <Field
                  label="Affectation"
                  name="service"
                  component={AutocompleteField}
                  required
                  items={buildListAsInputOptions(servicesList)}
                  disabled={missionNotEditable || !mission.employer}
                  onlySelect
                  multiple={false}
                />
              </Columns.Column>
              <Columns.Column>
                <Field
                  name="address"
                  label="Adresse de la prestation"
                  component={AutocompleteField}
                  required
                  items={buildListAsInputOptions(organizationQuery.data?.addresses ?? [])}
                  disabled={missionNotEditable || !mission.employer}
                />
              </Columns.Column>
            </Columns>
            {!missionNotEditable && (
              <>
                <RequestButton
                  type="submit"
                  color="primary"
                  mutation={updateMutation}
                  data-test="mission-info-panel-save"
                >
                  Enregistrer
                </RequestButton>
                <RequestMessage mutation={updateMutation} />
              </>
            )}
          </Form>
        </Formik>
      </Card.Content>
    </Card>
  )
}

export default MissionInfoPanel
